'use client';

import { useMemo, type PropsWithChildren } from 'react';
import { usePrivateContextState } from '@/hooks';
import {
  PrivateContextProvider,
  type PrivateContextData,
} from './PrivateContext';

/**
 * Owns the lifted `PrivateContext` state. Mounted above `<Shell>` from
 * `PrivateGate` so the navbar's user-aware bits keep seeing the same
 * context instance across the LOADING → AUTHENTICATED transition. The
 * auth-dependent side effects (session pull, org bootstrap, role
 * redirects, etc.) live in `<Private>` below `<Shell>` and write into
 * this lifted state via `setContextData` from `usePrivate()`.
 *
 * The setters (`setContextData`, `setSelectedOrganisation`, …) are merged
 * into the published context value at render time, not via a `useEffect`.
 * `Private` reads `setContextData` during its first render; relying on an
 * effect would make it `undefined` on render 1, and the initialisation
 * effect in `useInitializeContext` would no-op on its first run.
 */
export function PrivateStateProvider({ children }: PropsWithChildren) {
  const {
    contextData,
    setContextData,
    selectedOrganisation,
    setSelectedOrganisation,
    selectedOrganisationId,
    setSelectedOrganisationId,
    userOrganisations,
    setUserOrganisations,
    showSplashScreen,
    setShowSplashScreen,
  } = usePrivateContextState();

  const value = useMemo<PrivateContextData>(
    () => ({
      ...contextData,
      selectedOrganisation,
      setSelectedOrganisation,
      selectedOrganisationId,
      setSelectedOrganisationId,
      userOrganisations,
      setUserOrganisations,
      showSplashScreen,
      setShowSplashScreen,
      setContextData,
    }),
    [
      contextData,
      selectedOrganisation,
      setSelectedOrganisation,
      selectedOrganisationId,
      setSelectedOrganisationId,
      userOrganisations,
      setUserOrganisations,
      showSplashScreen,
      setShowSplashScreen,
      setContextData,
    ],
  );

  return (
    <PrivateContextProvider value={value}>{children}</PrivateContextProvider>
  );
}
